import { useState, useEffect } from "react";
import ComponentCard from "../../common/ComponentCard";
import Label from "../Label";
import Input from "../input/InputField";
import Select from "../Select";
import TextArea from "../input/TextArea";
import { useDropzone } from "react-dropzone";
import { Mail, Phone } from "lucide-react";
import Switch from "../switch/Switch";
import Radio from "../../../components/form/input/Radio";
import Button from "../../ui/button/Button";
import axios from "axios";
import { authService } from "../../../services/authService";
import { useNavigate } from "react-router";
import ImageCropModal from "../../common/ImageCropModal";
import { storageService } from "../../../services/storageService";

interface ProductFormData {
    name: string;
    description: string;
    defaultPrice: number;
    priceFormatted: string;
    category: string;
    type: string;
    companyId: string;
    pageUrl: string;
    imageUrl: string;
    supportInfo: {
        email: string;
        phone: string;
        refundPeriod: string;
        invoiceName: string;
    };
    status: string;
    imageKey?: string;
    customThankYouPageActive: boolean;
    customThankYouPageUrl: string;
}

interface NewProductFormProps {
    onSuccess: (data: any) => void;
    onError: (error: any) => void;
    companyId?: string;
}

// Função para aplicar máscara de telefone: (00) 00000-0000
const applyMaskPhone = (value: string) => {
    return value
        .replace(/\D/g, '')
        .replace(/^(\d{2})(\d)/g, '($1) $2')
        .replace(/(\d)(\d{4})$/, '$1-$2')
        .substr(0, 15);
};

const MAX_PRODUCT_PRICE = 10_000_000;

// Função para formatar o preço em tempo real no formato brasileiro
const formatPrice = (value: string): { formattedValue: string, numericValue: number } => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');

    // Converte para número (em centavos)
    let centsValue = parseInt(numbers) || 0;

    // Converte centavos para real
    let realValue = centsValue / 100;

    if (realValue > MAX_PRODUCT_PRICE) {
        realValue = MAX_PRODUCT_PRICE;
        centsValue = MAX_PRODUCT_PRICE * 100;
    }

    // Formata no padrão brasileiro
    const formatter = new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return {
        formattedValue: formatter.format(realValue),
        numericValue: realValue
    };
};

const INITIAL_PRODUCT_DATA: ProductFormData = {
    name: "",
    description: "",
    defaultPrice: 0,
    priceFormatted: "0,00",
    category: "",
    type: "",
    companyId: "",
    pageUrl: "",
    imageUrl: "",
    supportInfo: {
        email: "",
        phone: "",
        refundPeriod: "",
        invoiceName: ""
    },
    status: "active",
    imageKey: "",
    customThankYouPageActive: false,
    customThankYouPageUrl: ""
};

export default function NewProductForm({ onSuccess, onError }: NewProductFormProps) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<ProductFormData>(INITIAL_PRODUCT_DATA);

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [image, setImage] = useState<File | null>(null);
    const [tempImage, setTempImage] = useState<string | null>(null);
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [imageHover, setImageHover] = useState(false);
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);
    const [billingType, setBillingType] = useState<'single' | 'recurring'>('single');
    const [recurringFrequency, setRecurringFrequency] = useState('');
    const [firstChargeAmount, setFirstChargeAmount] = useState('');
    const [hasFirstChargeDifferent, setHasFirstChargeDifferent] = useState(false);
    const [firstChargeAmountFormatted, setFirstChargeAmountFormatted] = useState('');
    const [currency, setCurrency] = useState('BRL');
    const [checkoutLanguage, setCheckoutLanguage] = useState('pt');

    // Buscar o ID da empresa ao carregar o componente
    useEffect(() => {
        // Usar o método getCompanyId do authService que já trata todos os casos
        const companyId = authService.getCompanyId();

        if (companyId) {
            setFormData(prev => ({
                ...prev,
                companyId: companyId
            }));
        } else {
            // Fallback: tentar obter do usuário
            const user = authService.getUser();
            if (user?.companyId) {
                setFormData(prev => ({
                    ...prev,
                    companyId: user.companyId
                }));
            } else {
                console.warn('NewProductForm: Não foi possível obter o companyId do usuário');
                setApiError('Não foi possível identificar a empresa. Por favor, faça login novamente.');
            }
        }
    }, []);

    const currencyOptions = [
        { value: "BRL", label: "🇧🇷 R$ (BRL)" }
    ];

    const languageOptions = [
        { value: "pt", label: "🇧🇷 Português" },
        { value: "en", label: "🇺🇸 English" },
        { value: "es", label: "🇪🇸 Español" },
        { value: "fr", label: "🇫🇷 Français" },
        { value: "de", label: "🇩🇪 Deutsch" },
        { value: "it", label: "🇮🇹 Italiano" },
        { value: "nl", label: "🇳🇱 Nederlands" },
        { value: "ja", label: "🇯🇵 日本語 (Japonês)" },
        { value: "zh", label: "🇨🇳 中文 (Chinês)" },
        { value: "tr", label: "🇹🇷 Türkçe" },
        { value: "ru", label: "🇷🇺 Русский (Russo)" },
        { value: "ar", label: "🇸🇦 العربية (Árabe)" },
        { value: "he", label: "🇮🇱 עִבְרִית (Hebraico)" },
        { value: "pl", label: "🇵🇱 Polski" },
        { value: "hi", label: "🇮🇳 हिन्दी (Hindi)" }
    ];


    const recurringCycleOptions = [
        { value: 'monthly', label: 'Mensal' },
        { value: 'quarterly', label: 'Trimestral' },
        { value: 'semiannual', label: 'Semestral' },
        { value: 'annual', label: 'Anual' },
    ];

    const productTypeOptions = [
        { value: "course", label: "Curso" },
        { value: "subscription", label: "Assinatura" },
        { value: "ebook", label: "E-book" },
        { value: "consultancy", label: "Consultoria" },
        { value: "mentoring", label: "Mentoria" },
        { value: "other", label: "Outro" },
    ];

    const productCategoryOptions = [
        { value: "animals", label: "Animais (Pets)" },
        { value: "phoneApps", label: "Aplicativos para celular" },
        { value: "architecture", label: "Arquitetura" },
        { value: "cinema", label: "Cinema" },
        { value: "concentration", label: "Concentração" },
        { value: "cooking", label: "Culinária e Gastronomia" },
        { value: "personalDevelopment", label: "Desenvolvimento pessoal" },
        { value: "educational", label: "Educacional" },
        { value: "weightLoss", label: "Emagrecimento" },
        { value: "engineering", label: "Engenharia" },
        { value: "entertainment", label: "Entretenimento" },
        { value: "finances", label: "Finanças" },
        { value: "general", label: "Geral" },
        { value: "hobby", label: "Hobbie" },
        { value: "languages", label: "Idiomas" },
        { value: "imagesIconsPhotos", label: "Imagens / ícones / fotos" },
        { value: "children", label: "Infantil" },
        { value: "internet", label: "Internet" },
        { value: "investments", label: "Investimentos" },
        { value: "games", label: "Jogos" },
        { value: "legal", label: "Jurídico" },
        { value: "networkMarketing", label: "Marketing de Rede" },
        { value: "digitalMarketing", label: "Marketing Digital" },
        { value: "studyMethod", label: "Método de Estudo" },
        { value: "fashionBeauty", label: "Moda / Beleza / Estética" },
        { value: "music", label: "Música" },
        { value: "plantsGardening", label: "Plantas / Flores / Jardinagem" },
        { value: "relationships", label: "Relacionamento" },
        { value: "religious", label: "Religioso" },
        { value: "health", label: "Saúde" },
        { value: "sexuality", label: "Sexualidade" },
        { value: "software", label: "Software" },
        { value: "technology", label: "Tecnologia" },
        { value: "templates", label: "Templates" },
        { value: "tourism", label: "Turismo" }
    ];

    const productRefundOptions = [
        { value: "7", label: "7 dias" },
        { value: "15", label: "15 dias" },
        { value: "30", label: "30 dias" },
    ];

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { id, value } = e.target;

        // Limpar o erro específico quando o campo é modificado
        if (errors[id]) {
            setErrors({
                ...errors,
                [id]: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");

        // Mapeamento de IDs para estrutura do formData
        switch (id) {
            case "inputName":
                setFormData({ ...formData, name: value });
                break;
            case "inputPrice":
                const { formattedValue, numericValue } = formatPrice(value);
                setFormData({
                    ...formData,
                    defaultPrice: numericValue,
                    priceFormatted: formattedValue
                });
                break;
            case "inputUrl":
                setFormData({ ...formData, pageUrl: value });
                break;
            case "inputInvoiceName":
                setFormData({
                    ...formData,
                    supportInfo: { ...formData.supportInfo, invoiceName: value }
                });
                break;
            case "customThankYouPageUrl":
                setFormData({ ...formData, customThankYouPageUrl: value });
                break;
            default:
                break;
        }
    };

    const handleTextAreaChange = (value: string) => {
        setFormData({ ...formData, description: value });

        // Limpar o erro se o campo foi preenchido
        if (errors.description && value) {
            setErrors({
                ...errors,
                description: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleTypeChange = (value: string) => {
        setFormData({ ...formData, type: value });

        // Limpar o erro se o campo foi preenchido
        if (errors.type && value) {
            setErrors({
                ...errors,
                type: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleCategoryChange = (value: string) => {
        setFormData({ ...formData, category: value });

        // Limpar o erro se o campo foi preenchido
        if (errors.category && value) {
            setErrors({
                ...errors,
                category: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleRefundPeriodChange = (value: string) => {
        setFormData({
            ...formData,
            supportInfo: { ...formData.supportInfo, refundPeriod: value }
        });

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const onDrop = (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];

            if (file.size > 2 * 1024 * 1024) {
                setApiError("A imagem deve ter no máximo 2MB");
                return;
            }

            // Ler o arquivo e abrir o modal de recorte
            const reader = new FileReader();
            reader.onload = () => {
                setTempImage(reader.result as string);
                setCropModalOpen(true);
            };
            reader.readAsDataURL(file);

            // Limpar mensagens de API quando o usuário faz alterações
            if (apiError) setApiError("");
            if (apiSuccess) setApiSuccess("");
        }
    };

    // Função para tratar o cancelamento do recorte
    const handleCropCancel = () => {
        setCropModalOpen(false);
        setTempImage(null);
    };

    // Função para tratar a conclusão do recorte
    const handleCropComplete = (croppedImage: string) => {
        setCropModalOpen(false);

        // Converter dataURL para Blob/File para upload posterior
        const byteString = atob(croppedImage.split(',')[1]);
        const mimeType = croppedImage.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);

        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        const blob = new Blob([ab], { type: mimeType });
        const fileName = 'imagem_produto.jpg';
        const croppedFile = new File([blob], fileName, { type: mimeType });

        setImage(croppedFile);
        setTempImage(croppedImage); // Manter a imagem recortada para exibição
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "image/png": [],
            "image/jpeg": [],
            "image/webp": [],
            "image/svg+xml": [],
        },
        maxSize: 2 * 1024 * 1024, // 2MB
    });

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isValidEmail = emailRegex.test(email);

        if (!isValidEmail && email.trim() !== '') {
            setErrors({
                ...errors,
                email: "Este é um endereço de email inválido."
            });
        } else {
            const newErrors = { ...errors };
            delete newErrors.email;
            setErrors(newErrors);
        }

        return isValidEmail;
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFormData({
            ...formData,
            supportInfo: { ...formData.supportInfo, email: value }
        });

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    // Validação de email no blur para melhor experiência do usuário
    const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        validateEmail(e.target.value);
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const maskedValue = applyMaskPhone(value);

        setFormData({
            ...formData,
            supportInfo: { ...formData.supportInfo, phone: maskedValue }
        });

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        // Validar companyId (campo crítico)
        if (!formData.companyId) {
            newErrors.companyId = "Erro: Não foi possível identificar a empresa. Por favor, faça login novamente.";
            setApiError("Erro: Não foi possível identificar a empresa. Por favor, faça login novamente.");
        }

        if (!formData.name) {
            newErrors.inputName = "O nome do produto é obrigatório";
        }

        if (!formData.description) {
            newErrors.description = "A descrição do produto é obrigatória";
        }

        if (!formData.type) {
            newErrors.type = "O tipo do produto é obrigatório";
        }

        if (!formData.category) {
            newErrors.category = "A categoria do produto é obrigatória";
        }

        if (!formData.defaultPrice || formData.defaultPrice <= 0) {
            newErrors.inputPrice = "O preço do produto é obrigatório e deve ser maior que zero";
        } else if (formData.defaultPrice > MAX_PRODUCT_PRICE) {
            newErrors.inputPrice = "O valor máximo permitido é R$ 10.000.000,00";
        }

        if (!currency) {
            newErrors.currency = "A moeda é obrigatória";
        }

        if (!checkoutLanguage) {
            newErrors.checkoutLanguage = "O idioma do checkout é obrigatório";
        }

        // Validar URL da página de vendas
        if (!formData.pageUrl) {
            newErrors.inputUrl = "A URL da página de vendas é obrigatória";
        }

        // Validar imagem de capa do produto
        if (!image && !tempImage && !formData.imageUrl) {
            newErrors.inputImage = "A imagem de capa do produto é obrigatória";
        }

        // Validar prazo de reembolso
        if (!formData.supportInfo.refundPeriod) {
            newErrors.refundPeriod = "O prazo de reembolso é obrigatório";
        }

        // Validar nome na fatura
        if (!formData.supportInfo.invoiceName) {
            newErrors.inputInvoiceName = "O nome na fatura é obrigatório";
        }

        // Validar e-mail de suporte
        if (!formData.supportInfo.email) {
            newErrors.email = "O e-mail de suporte é obrigatório";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.supportInfo.email)) {
            newErrors.email = "O e-mail informado é inválido";
        }

        // Validar telefone de contato do suporte
        if (!formData.supportInfo.phone) {
            newErrors.phone = "O telefone de contato do suporte é obrigatório";
        }

        // Validar campos condicionais
        // Validar URL da página de obrigado apenas se estiver ativa
        if (formData.customThankYouPageActive && !formData.customThankYouPageUrl) {
            newErrors.customThankYouPageUrl = "Informe a URL da sua página de obrigado";
        }

        // Validar frequência apenas se o tipo de cobrança for recorrente
        if (billingType === 'recurring' && !recurringFrequency) {
            newErrors.recurringFrequency = "A frequência da assinatura é obrigatória";
        }

        // Validar valor da primeira cobrança apenas se for diferente e estiver ativo
        if (billingType === 'recurring' && hasFirstChargeDifferent && !firstChargeAmount) {
            newErrors.firstChargeAmount = "Informe o valor da primeira cobrança";
        }

        setErrors(newErrors);

        // Se não houver erros, retorna true
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }

        // Validar formulário
        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            // Primeiro, faz upload da imagem se existir
            let imageData = null;
            if (image) {
                setUploadProgress(0);

                imageData = await storageService.uploadFile(
                    image,
                    'products',
                    `product-${formData.name.replace(/\s+/g, '-').toLowerCase()}`
                );

                if (!imageData.success) {
                    throw new Error(imageData.error || 'Falha ao fazer upload da imagem');
                }

                setUploadProgress(100);
            }

            // Verificação de segurança do companyId antes de enviar
            const finalCompanyId = formData.companyId || authService.getCompanyId();

            if (!finalCompanyId) {
                throw new Error('Não foi possível identificar a empresa. Por favor, faça login novamente.');
            }

            // Prepara os dados para enviar
            const productData = {
                ...formData,
                companyId: finalCompanyId,
                imageUrl: imageData?.url || formData.imageUrl,
                imageKey: imageData?.key || formData.imageKey,
                price: parseFloat(formData.priceFormatted.replace(/\./g, '').replace(',', '.')) || 0,
                currency: currency,
                checkoutLanguage: checkoutLanguage,
                billingType: billingType,
                recurringFrequency: billingType === 'recurring' ? recurringFrequency : null,
                firstChargeAmount: billingType === 'recurring' && hasFirstChargeDifferent ? parseFloat(firstChargeAmount) : null,
            };

            // Envia os dados do produto para a API
            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/api/products`,
                productData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            if (response.status === 201) {
                // Chamar o callback de sucesso
                onSuccess(response.data);
            }
        } catch (error: any) {
            console.error("Erro ao criar produto:", error);

            // Chamar o callback de erro
            onError(error);

            setLoading(false);
        }
    };

    const handleSwitchChange = (id: string, checked: boolean) => {
        if (id === "status") {
            setFormData({
                ...formData,
                status: checked ? "active" : "draft"
            });
        } else {
            setFormData({
                ...formData,
                [id]: checked
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    // Mostrar a imagem do produto 
    const getImagePreview = () => {
        if (tempImage) {
            return (
                <div className="flex flex-col items-center">
                    <div
                        className="mb-4 w-48 h-48 overflow-hidden rounded-lg relative group"
                        onMouseEnter={() => setImageHover(true)}
                        onMouseLeave={() => setImageHover(false)}
                    >
                        <img
                            src={tempImage}
                            alt="Imagem do produto"
                            className="w-full h-full object-contain"
                        />

                        {/* Botão de excluir - aparece apenas no hover */}
                        <div className={`absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center transition-opacity duration-200 ${imageHover ? 'opacity-100' : 'opacity-0'}`}>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation(); // Evita acionar o dropzone
                                    handleRemoveImage();
                                }}
                                className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
                                title="Remover imagem"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" />
                                </svg>
                            </button>
                        </div>
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-400">
                        Clique para alterar a imagem
                    </span>
                </div>
            );
        }

        return (
            <div className="dz-message flex flex-col items-center m-0!">
                {/* Icon Container */}
                <div className="mb-[22px] flex justify-center">
                    <div className="flex h-[68px] w-[68px] items-center justify-center rounded-full bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-400">
                        <svg
                            className="fill-current"
                            width="29"
                            height="28"
                            viewBox="0 0 29 28"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M14.5019 3.91699C14.2852 3.91699 14.0899 4.00891 13.953 4.15589L8.57363 9.53186C8.28065 9.82466 8.2805 10.2995 8.5733 10.5925C8.8661 10.8855 9.34097 10.8857 9.63396 10.5929L13.7519 6.47752V18.667C13.7519 19.0812 14.0877 19.417 14.5019 19.417C14.9161 19.417 15.2519 19.0812 15.2519 18.667V6.48234L19.3653 10.5929C19.6583 10.8857 20.1332 10.8855 20.426 10.5925C20.7188 10.2995 20.7186 9.82463 20.4256 9.53184L15.0838 4.19378C14.9463 4.02488 14.7367 3.91699 14.5019 3.91699ZM5.91626 18.667C5.91626 18.2528 5.58047 17.917 5.16626 17.917C4.75205 17.917 4.41626 18.2528 4.41626 18.667V21.8337C4.41626 23.0763 5.42362 24.0837 6.66626 24.0837H22.3339C23.5766 24.0837 24.5839 23.0763 24.5839 21.8337V18.667C24.5839 18.2528 24.2482 17.917 23.8339 17.917C23.4197 17.917 23.0839 18.2528 23.0839 18.667V21.8337C23.0839 22.2479 22.7482 22.5837 22.3339 22.5837H6.66626C6.25205 22.5837 5.91626 22.2479 5.91626 21.8337V18.667Z"
                            />
                        </svg>
                    </div>
                </div>

                {/* Text Content */}
                <h4 className="mb-3 font-semibold text-gray-800 text-theme-xl dark:text-white/90">
                    {isDragActive ? "Drop Files Here" : "Arraste um arquivo"}
                </h4>

                <span className="text-center mb-3 block w-full max-w-[290px] text-sm text-gray-700 dark:text-gray-400">
                    {image ? image.name : "Arraste e solte arquivos JPG, JPEG, PNG, WebP, GIF, de no máximo 2MB, ou"}
                </span>

                <span className="font-medium underline text-theme-sm text-brand-500">
                    Clique para procurar...
                </span>
            </div>
        );
    };

    const handleRemoveImage = () => {
        setImage(null);
        setTempImage(null);
    };

    return (
        <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="col-span-1 lg:col-span-2">
                    <ComponentCard title="Criar Novo Produto">
                        {apiError && (
                            <div className="bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4">
                                {apiError}
                            </div>
                        )}

                        {apiSuccess && (
                            <div className="bg-green-50 dark:bg-green-900/10 text-green-600 dark:text-green-400 p-3 rounded-lg mb-4">
                                {apiSuccess}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div>
                                <Switch
                                    label="Vendas ativas"
                                    defaultChecked={true}
                                    onChange={(checked) => handleSwitchChange("status", checked)}
                                />
                            </div>

                            <div className="mt-5">
                                <Label htmlFor="inputName">Nome do Produto *</Label>
                                <Input
                                    type="text"
                                    id="inputName"
                                    placeholder="Informe o nome do seu produto"
                                    onChange={handleInputChange}
                                    value={formData.name}
                                    error={!!errors.inputName}
                                    hint={errors.inputName}
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3 mt-6">
                                <div>
                                    <Label htmlFor="inputPrice">Preço do Produto *</Label>
                                    <Input
                                        type="text"
                                        id="inputPrice"
                                        placeholder="0,00"
                                        onChange={handleInputChange}
                                        value={formData.priceFormatted}
                                        error={!!errors.inputPrice}
                                        hint={errors.inputPrice}
                                    />
                                </div>
                                <div>
                                    <Label>Moeda *</Label>
                                    <Select
                                        options={currencyOptions}
                                        placeholder="Selecione uma opção"
                                        defaultValue="BRL"
                                        onChange={(value) => setCurrency(value)}
                                        className={`dark:bg-dark-900 ${errors.currency ? 'border-red-500' : ''}`}
                                    />
                                    {errors.currency && <p className="mt-1 text-sm text-red-500">{errors.currency}</p>}
                                </div>
                                <div>
                                    <Label>Idioma do Checkout *</Label>
                                    <Select
                                        options={languageOptions}
                                        placeholder="Selecione uma opção"
                                        defaultValue="pt"
                                        onChange={(value) => setCheckoutLanguage(value)}
                                        className={`dark:bg-dark-900 ${errors.checkoutLanguage ? 'border-red-500' : ''}`}
                                    />
                                    {errors.checkoutLanguage && <p className="mt-1 text-sm text-red-500">{errors.checkoutLanguage}</p>}
                                </div>
                            </div>

                            <div className="mt-6">
                                <Label>Tipo de cobrança *</Label>
                                <div className="flex flex-row gap-6 mt-5">
                                    <Radio
                                        id="billingType-single"
                                        name="billingType"
                                        value="single"
                                        checked={billingType === 'single'}
                                        label="Única"
                                        onChange={() => setBillingType('single')}
                                    />
                                    <Radio
                                        id="billingType-recurring"
                                        name="billingType"
                                        value="recurring"
                                        checked={billingType === 'recurring'}
                                        label="Recorrente"
                                        onChange={() => setBillingType('recurring')}
                                    />
                                </div>

                                {billingType === 'recurring' && (
                                    <>
                                        <div className="mt-6">
                                            <Label>Frequência da assinatura *</Label>
                                            <Select
                                                options={recurringCycleOptions}
                                                placeholder="Selecione a frequência"
                                                onChange={value => setRecurringFrequency(value)}
                                                value={recurringFrequency}
                                                className={`dark:bg-dark-900 ${errors.recurringFrequency ? 'border-red-500' : ''}`}
                                            />
                                            {errors.recurringFrequency && (
                                                <p className="mt-1 text-sm text-red-500">{errors.recurringFrequency}</p>
                                            )}
                                        </div>
                                        <div className="mt-6">
                                            <Switch
                                                label="Valor diferente na primeira cobrança"
                                                checked={hasFirstChargeDifferent}
                                                onChange={checked => setHasFirstChargeDifferent(checked)}
                                            />
                                            {hasFirstChargeDifferent && (
                                                <div className="mt-6">
                                                    <Label>Valor da primeira cobrança *</Label>
                                                    <Input
                                                        type="text"
                                                        placeholder="Ex: 10,00"
                                                        value={firstChargeAmountFormatted}
                                                        onChange={e => {
                                                            // Aqui assume que você usa o formatPrice igual ao preço do produto
                                                            const { formattedValue, numericValue } = formatPrice(e.target.value);
                                                            setFirstChargeAmountFormatted(formattedValue);
                                                            setFirstChargeAmount(numericValue.toString());

                                                            // Limpar o erro quando o usuário digitar
                                                            if (errors.firstChargeAmount) {
                                                                setErrors({
                                                                    ...errors,
                                                                    firstChargeAmount: ""
                                                                });
                                                            }
                                                        }}
                                                        error={!!errors.firstChargeAmount}
                                                        hint={errors.firstChargeAmount}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="mt-8">
                                <Label htmlFor="inputUrl">Url da Página de Vendas *</Label>
                                <Input
                                    type="text"
                                    id="inputUrl"
                                    placeholder="Ex.: https://minhapagina.com.br"
                                    onChange={handleInputChange}
                                    value={formData.pageUrl}
                                    error={!!errors.inputUrl}
                                    hint={errors.inputUrl}
                                />
                            </div>

                            <div className="mt-8 grid grid-cols-1 md:grid-cols-1 gap-4">
                                <Switch
                                    label="Ativar página de obrigado personalizada"
                                    defaultChecked={false}
                                    onChange={(checked) => handleSwitchChange("customThankYouPageActive", checked)}
                                />
                            </div>

                            {formData.customThankYouPageActive && (
                                <div className="mt-6">
                                    <Label htmlFor="customThankYouPageUrl">URL da página de obrigado personalizada *</Label>
                                    <Input
                                        type="text"
                                        id="customThankYouPageUrl"
                                        placeholder="Ex: https://minhapagina.com.br/obrigado"
                                        onChange={handleInputChange}
                                        error={!!errors.customThankYouPageUrl}
                                        hint={errors.customThankYouPageUrl}
                                    />
                                </div>
                            )}

                            <div className="mt-8">
                                <Label htmlFor="inputDescription">Descrição do Produto *</Label>
                                <TextArea
                                    value={formData.description}
                                    onChange={handleTextAreaChange}
                                    rows={6}
                                    placeholder="Informe do que ele se trata e o que oferece de forma clara e breve."
                                    error={!!errors.description}
                                    hint={errors.description}
                                />
                            </div>

                            <Label htmlFor="inputImage" className="mt-5 block">Imagem de Capa do Produto *</Label>
                            {errors.inputImage && <p className="mt-1 text-sm text-red-500">{errors.inputImage}</p>}

                            <div className={`transition border ${errors.inputImage ? 'border-red-500' : 'border-gray-300'} border-dashed cursor-pointer dark:hover:border-brand-500 dark:border-gray-700 rounded-xl hover:border-brand-500`}>

                                <div
                                    {...getRootProps()}
                                    className={`dropzone rounded-xl border-dashed border-gray-300 p-7 lg:p-10
                                ${isDragActive
                                            ? "border-brand-500 bg-gray-100 dark:bg-gray-800"
                                            : "border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-900"
                                        }`}
                                    id="inputImage"
                                >
                                    {/* Hidden Input */}
                                    <input {...getInputProps()} />

                                    {getImagePreview()}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 mt-5">
                                <div>
                                    <Label>Tipo de Produto *</Label>
                                    <Select
                                        options={productTypeOptions}
                                        placeholder="Selecione uma opção"
                                        onChange={handleTypeChange}
                                        className={`dark:bg-dark-900 ${errors.type ? 'border-red-500' : ''}`}
                                    />
                                    {errors.type && <p className="mt-1 text-sm text-red-500">{errors.type}</p>}
                                </div>

                                <div>
                                    <Label>Categoria do Produto *</Label>
                                    <Select
                                        options={productCategoryOptions}
                                        placeholder="Selecione uma opção"
                                        onChange={handleCategoryChange}
                                        className={`dark:bg-dark-900 ${errors.category ? 'border-red-500' : ''}`}
                                    />
                                    {errors.category && <p className="mt-1 text-sm text-red-500">{errors.category}</p>}
                                </div>

                                <div>
                                    <Label>Prazo de Reembolso *</Label>
                                    <Select
                                        options={productRefundOptions}
                                        placeholder="Selecione uma opção"
                                        onChange={handleRefundPeriodChange}
                                        className={`dark:bg-dark-900 ${errors.refundPeriod ? 'border-red-500' : ''}`}
                                    />
                                    {errors.refundPeriod && <p className="mt-1 text-sm text-red-500">{errors.refundPeriod}</p>}
                                </div>

                                <div>
                                    <Label htmlFor="inputInvoiceName">Nome na Fatura *</Label>
                                    <Input
                                        type="text"
                                        id="inputInvoiceName"
                                        placeholder="Informe o nome que irá aparecer na fatura do cartão"
                                        onChange={handleInputChange}
                                        value={formData.supportInfo.invoiceName}
                                        error={!!errors.inputInvoiceName}
                                        hint={errors.inputInvoiceName}
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="email">E-mail de contato do suporte *</Label>
                                    <div className="relative">
                                        <Input
                                            placeholder="E-mail de contato do suporte"
                                            type="email"
                                            id="email"
                                            className={`pl-[62px] ${errors.email ? 'border-red-500' : ''}`}
                                            value={formData.supportInfo.email}
                                            onChange={handleEmailChange}
                                            onBlur={handleEmailBlur}
                                        />
                                        <span className="absolute left-0 top-1/2 -translate-y-1/2 border-r border-gray-200 px-3.5 py-3 text-gray-500 dark:border-gray-800 dark:text-gray-400">
                                            <Mail className="size-4" />
                                        </span>
                                    </div>
                                    {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                                </div>

                                <div>
                                    <Label>Telefone de contato do suporte *</Label>
                                    <div className="relative">
                                        <Input
                                            placeholder="(99) 99999-9999"
                                            type="text"
                                            id="phone"
                                            value={formData.supportInfo.phone}
                                            onChange={handlePhoneChange}
                                            className={`pl-[62px] ${errors.phone ? 'border-red-500' : ''}`}
                                        />
                                        <span className="absolute left-0 top-1/2 -translate-y-1/2 border-r border-gray-200 px-3.5 py-3 text-gray-500 dark:border-gray-800 dark:text-gray-400">
                                            <Phone className="size-4" />
                                        </span>
                                    </div>
                                    {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end gap-4.5">
                                <Button
                                    size="md"
                                    variant="outline"
                                    onClick={() => navigate("/products")}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    size="md"
                                    variant="primary"
                                    onClick={() => handleSubmit()}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            {uploadProgress > 0 && uploadProgress < 100 ? (
                                                <span>Enviando imagem: {uploadProgress}%</span>
                                            ) : (
                                                <span>Salvando...</span>
                                            )}
                                        </>
                                    ) : (
                                        "Salvar"
                                    )}
                                </Button>
                            </div>
                        </form>
                    </ComponentCard >
                </div>
            </div>

            {/* Modal de crop */}
            {cropModalOpen && tempImage && (
                <ImageCropModal
                    imageSrc={tempImage}
                    open={cropModalOpen}
                    onClose={handleCropCancel}
                    onCropComplete={handleCropComplete}
                />
            )}
        </>
    );
}